<div class="thread" #scrollContainer>
  <div class="thread-inner">
    @for (message of messages; track trackById(messageIndex, message); let messageIndex = $index) {
      @if (message.role === ChatMessageRole.User) {
        <div class="msg-user-wrap"
          [class.editing]="editingMessageId === message.id">
          @if (editingMessageId === message.id) {
            <div class="msg-user-edit">
              <textarea #editTextarea rows="2" [value]="editInput" [attr.maxlength]="maxLength" (input)="onEditInput($event)"
              (keydown.escape)="cancelEdit()"></textarea>
              <div class="msg-user-edit-actions">
                <button type="button" class="edit-cancel-btn" (click)="cancelEdit()">{{ 'Cancel' | translate }}</button>
                <button type="button" class="edit-save-btn" [disabled]="!editHasContent || editCharCount > maxLength"
                (click)="saveEdit(message.id)">{{ 'Save' | translate }}</button>
              </div>
            </div>
          } @else {
            <div class="msg-user">{{ message.content }}</div>
            <div class="msg-user-footer">
              @if (message.id === lastUserMessageId && !isTurnInFlight) {
                <button type="button" class="action-btn edit-btn"
                  [matTooltip]="'Edit' | translate" (click)="startEdit(message)">
                  <mat-icon>edit</mat-icon>
                </button>
              }
              <span class="msg-time">{{ message.createdAt | dateFormat }}</span>
            </div>
            @if (askCancelled && message.id === lastUserMessageId) {
              <div class="msg-stopped-marker">
                {{ 'You stopped this answer.' | translate }}
              </div>
            }
          }
        </div>
      } @else {
        <div class="msg-ai">
          <div class="ai-av">
            <mat-icon>auto_awesome</mat-icon>
          </div>
          <div class="ai-card">
            @if (message.kind === ChatMessageKind.Report) {
              <div class="report-badge">
                <mat-icon>description</mat-icon>
                {{ 'Report' | translate }}
              </div>
            }
            @if (turnSummaryViewByMessageId[message.id]; as summary) {
              <div class="progress-summary" [class.expanded]="expandedSummaryMessageIds[message.id]">
                <button type="button" class="progress-summary-toggle" (click)="toggleTurnSummary(message.id)">
                  <mat-icon class="summary-check">check_circle</mat-icon>
                  <span>{{ 'Worked for {{seconds}}s · {{count}} step(s)' | translate: { seconds: summary.totalSeconds, count: summary.stepCount } }}</span>
                  <mat-icon class="summary-chevron">expand_more</mat-icon>
                </button>
                @if (expandedSummaryMessageIds[message.id]) {
                  <div class="progress-summary-list">
                    @for (step of summary.steps; track trackByIndex($index)) {
                      <div class="progress-summary-item">
                        <span>{{ step.labelKey | translate }}@if (step.count > 1) {<span class="progress-step-count"> ×{{ step.count }}</span>}</span>
                        <span class="progress-summary-duration">{{ step.durationText }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
            <app-answer-renderer [content]="message.content" [citationNumbering]="citationNumberingByIndex[messageIndex]"
              [isReport]="message.kind === ChatMessageKind.Report"></app-answer-renderer>
            <app-analyst-output [outputs]="message.outputs" [citationNumbering]="citationNumberingByIndex[messageIndex]" [disabled]="isAsking"
              [clarificationAnswered]="clarificationAnsweredByIndex[messageIndex]"
              [clarificationAnswerText]="clarificationAnswerTextByIndex[messageIndex]"
            (optionSelected)="optionSelected.emit($event)"></app-analyst-output>
            @if (turnCaveatKeyByIndex[messageIndex]; as turnCaveatKey) {
              <div class="turn-caveat">
                <mat-icon>warning_amber</mat-icon>
                <span>{{ turnCaveatKey | translate }}</span>
              </div>
            }
            <div class="msg-time">{{ message.createdAt | dateFormat }}</div>
            @if (feedbackThanksMessageId !== message.id) {
              <div class="msg-actions">
                <button type="button" class="action-btn copy-btn" [class.copied]="copiedMessageId === message.id"
                  [matTooltip]="(copiedMessageId === message.id ? 'Copied' : 'Copy to clipboard') | translate"
                  (click)="copyAnswer(message)">
                  <mat-icon>{{ copiedMessageId === message.id ? 'check' : 'content_copy' }}</mat-icon>
                </button>
                <button type="button" class="action-btn feedback-btn" [class.active]="feedbackByMessageId[message.id] === feedbackRating.Up"
                  [matTooltip]="'Helpful' | translate" (click)="onThumbsUp(message)">
                  <mat-icon>thumb_up</mat-icon>
                </button>
                <button type="button" class="action-btn feedback-btn" [class.active]="feedbackByMessageId[message.id] === feedbackRating.Down"
                  [matTooltip]="'Not helpful' | translate" (click)="onThumbsDown(message)">
                  <mat-icon>thumb_down</mat-icon>
                </button>
                @if (message.id === lastAssistantMessageId && !isTurnInFlight) {
                  <button type="button" class="action-btn regenerate-btn"
                    [matTooltip]="'Regenerate' | translate" (click)="onRegenerateClick(message)">
                    <mat-icon>refresh</mat-icon>
                  </button>
                }
                @if (message.kind === ChatMessageKind.Report) {
                  <button type="button" class="action-btn export-btn" [matTooltip]="'Export as PDF' | translate"
                    (click)="onExportClick(message, ChatMessageExportFormat.Pdf)">
                    <mat-icon>picture_as_pdf</mat-icon>
                  </button>
                  <button type="button" class="action-btn export-btn" [matTooltip]="'Export as Excel' | translate"
                    (click)="onExportClick(message, ChatMessageExportFormat.Xlsx)">
                    <mat-icon>table_chart</mat-icon>
                  </button>
                }
              </div>
            } @else {
              <span class="feedback-thanks">{{ 'Thanks for the feedback' | translate }}</span>
            }
            @if (feedbackPopupMessageId === message.id) {
              <div class="feedback-popup" [exclude]="'.feedback-btn'"
                (clickOutside)="cancelFeedbackPopup()">
                <textarea rows="2" [(ngModel)]="feedbackReasonInput" [placeholder]="'Tell us more…' | translate"
                (keyup.escape)="cancelFeedbackPopup()"></textarea>
                <label class="feedback-popup-minutes">
                  {{ 'Minutes this saved you (optional)' | translate }}
                  <input type="number" min="0" max="1440" step="1" [(ngModel)]="feedbackMinutesSavedInput"
                    #minutesSaved="ngModel" (keyup.escape)="cancelFeedbackPopup()">
                  </label>
                  @if (minutesSaved.invalid) {
                    <span class="feedback-popup-error">
                      {{ 'Enter a value between 0 and 1440.' | translate }}
                    </span>
                  }
                  <div class="feedback-popup-actions">
                    <button type="button" class="feedback-popup-cancel" (click)="cancelFeedbackPopup()">{{ 'Cancel' | translate }}</button>
                    <button type="button" class="feedback-popup-submit" [disabled]="minutesSaved.invalid"
                    (click)="submitFeedbackReason(message)">{{ 'Submit' | translate }}</button>
                  </div>
                </div>
              }
            </div>
          </div>
        }
      }
      <ng-template #progressIndicatorTpl>
        @if (progressSteps.length > 0) {
          <div class="progress-timeline">
            @for (step of progressStepViews; track trackByIndex($index)) {
              <div class="progress-step">
                <div class="progress-step-gutter">
                  <span class="progress-step-line"></span>
                  <span class="progress-step-node done">
                    <mat-icon>check</mat-icon>
                  </span>
                </div>
                <div class="progress-step-text">
                  <span>{{ step.labelKey | translate }}@if (step.count > 1) {<span class="progress-step-count"> ×{{ step.count }}</span>}</span>
                  <span class="progress-step-duration">{{ step.durationText }}</span>
                </div>
              </div>
            }
            <div class="progress-step">
              <div class="progress-step-gutter">
                <span class="progress-step-line"></span>
                <span class="progress-step-node current">
                  <span class="spinner spinner-sm"></span>
                </span>
              </div>
              <div class="progress-step-text current">
                <span>{{ progressLabel | translate }}@if (progressCurrentCount > 1) {<span class="progress-step-count"> ×{{ progressCurrentCount }}</span>}</span>
              </div>
            </div>
          </div>
        } @else {
          <div class="working">
            <span class="spinner"></span>
            <span class="shimmer-text">{{ progressLabel | translate }}</span>
          </div>
        }
      </ng-template>
      @if (isAsking) {
        <div class="msg-ai">
          <div class="ai-av">
            <mat-icon>auto_awesome</mat-icon>
          </div>
          <div class="ai-card">
            <ng-container *ngTemplateOutlet="progressIndicatorTpl"></ng-container>
            <button type="button" class="stop-ask-btn" [disabled]="askStopping" (click)="onStopAskClick()">
              <mat-icon>close</mat-icon>
              {{ (askStopping ? 'Stopping…' : 'Stop') | translate }}
            </button>
          </div>
        </div>
      }
      @if (reportJobStatus === AiReportJobStatus.Queued || reportJobStatus === AiReportJobStatus.Running) {
        <div class="msg-ai">
          <div class="ai-av">
            <mat-icon>description</mat-icon>
          </div>
          <div class="ai-card">
            <ng-container *ngTemplateOutlet="progressIndicatorTpl"></ng-container>
            <button type="button" class="cancel-report-btn" [disabled]="reportCancelRequested" (click)="onCancelReportClick()">
              <mat-icon>close</mat-icon>
              {{ (reportCancelRequested ? 'Cancelling…' : 'Cancel') | translate }}
            </button>
          </div>
        </div>
      }
      @if (reportJobStatus === AiReportJobStatus.Failed && reportConnectionLost) {
        <div class="msg-ai">
          <div class="ai-av">
            <mat-icon>wifi_off</mat-icon>
          </div>
          <div class="ai-card">
            <div class="report-status-title">{{ 'Connection lost' | translate }}</div>
            <div class="report-status-message">
              {{ 'We lost the connection to the server. If the report was still generating, it may finish on its own. Reload the page to check before trying again.' | translate }}
            </div>
          </div>
        </div>
      }
      @if (reportJobStatus === AiReportJobStatus.Cancelled || (reportJobStatus === AiReportJobStatus.Failed && !reportConnectionLost)) {
        <div class="msg-ai">
          <div class="ai-av">
            <mat-icon>description</mat-icon>
          </div>
          <div class="ai-card">
            <div class="report-status-title">
              {{ (reportJobStatus === AiReportJobStatus.Failed ? 'Report generation failed' : 'Report generation cancelled') | translate }}
            </div>
            @if (reportJobStatus === AiReportJobStatus.Failed) {
              <div class="report-status-message">
                {{ (reportJobErrorMessage || 'Something went wrong while generating your report. Please try again.') | translate }}
              </div>
            }
          </div>
        </div>
      }
      @if (turnError !== null && !isAsking) {
        <app-chat-turn-error [kind]="turnError" [message]="turnErrorMessage" [retryAfterSeconds]="turnRetryAfterSeconds" (retry)="onRetry()"></app-chat-turn-error>
      }
    </div>
  </div>
